/**
 * Cost columns for the merged offer table (`offer-unified-table` capability,
 * CU-86cbc3r8b). These fields are read-only and masked per row using the
 * row's own `costsWithheld` marker — never a caller-level `canViewCosts`
 * flag, and never the row's `supplierWithheld` marker, per `field-visibility`'s
 * "Restricted values are masked, not removed from the layout" and "The two
 * permissions are resolved independently".
 *
 * The nine OFFER_PRICE_OVERRIDE_FIELD_IDS (@ooh/contracts) client price
 * fields — catalogPrice/discountedPrice/lastMinutePrice/starterPrice/
 * clientProduction/clientInstallation/clientDismantling/
 * clientAdditionalFees/muralEcoProduction — moved to
 * offer-unit-price-columns.tsx (offer-formatka-prices, CU-86cbgnt38): they
 * are now editable per unit within an offer, while the remaining internal
 * cost fields here stay exactly as they were — read-only, belonging to the
 * unit rather than the offer.
 *
 * There is no `CATALOG_FIELD_REGISTRY`/`getOfferMediaUnitFields` entry for
 * cost fields — cost columns are a separate mechanism, mirroring the
 * catalog's own `COST_COLUMN_IDS`/`columns-extended.tsx` split
 * (`apps/web/src/features/media-units/lib/columns.tsx`).
 */
import type { ColumnDef } from '@ooh/ui/components/data-table';
import { maskFieldValue } from '@ooh/ui/components/masked-value';
import { COST_RANGE_DIMENSIONS, OFFER_PRICE_OVERRIDE_FIELD_IDS } from '@ooh/contracts';
import type { CostRangeDimensionId } from '@ooh/contracts';
import type { TFunction } from 'i18next';
import { formatPrice } from '../../../media-units/lib/formatters';
import type { OfferUnitItem } from '../units-tab-columns';

const OVERRIDE_FIELD_ID_SET = new Set<string>(OFFER_PRICE_OVERRIDE_FIELD_IDS);

/**
 * Every internal cost field this surface still shows read-only — every
 * COST_RANGE_DIMENSIONS entry EXCEPT the nine that moved to
 * offer-unit-price-columns.tsx (see this file's header doc comment). All of
 * them stay gated by the row's own `costsWithheld` marker; none of them is
 * client-facing any more (the client-facing ones moved out).
 */
export const OFFER_UNIT_COST_COLUMN_IDS: readonly CostRangeDimensionId[] =
  COST_RANGE_DIMENSIONS.filter((d) => !OVERRIDE_FIELD_ID_SET.has(d.id)).map((d) => d.id);

type OfferUnitCostFieldId = CostRangeDimensionId;

function buildCostColumn(
  id: OfferUnitCostFieldId,
  label: string,
  explanation: string,
): ColumnDef<OfferUnitItem> {
  return {
    id,
    label,
    align: 'right',
    width: 120,
    sortable: true,
    sortKey: id,
    renderCell: (item) => {
      const value = (item[id] ?? null) as number | null;
      // `rent` carries its own withheld marker, independent of costsWithheld
      // (field-visibility: rent requires cost.view AND the wildcard-only
      // cost.view_rent grant) — every other id here still reads costsWithheld.
      const withheld = id === 'rent' ? item.rentWithheld : item.costsWithheld;
      return maskFieldValue(
        value,
        withheld,
        explanation,
        <span className="font-mono text-xs">{formatPrice(value)}</span>,
      );
    },
  };
}

/**
 * Builds the internal cost columns for the merged table — read-only for
 * every caller (a unit's costs belong to the unit, not the offer that
 * includes it), masked per the row's own `costsWithheld` marker.
 */
export function buildOfferUnitCostColumns(t: TFunction<'offers'>): ColumnDef<OfferUnitItem>[] {
  const explanation = t('workspace.unitsTab.costs.maskedExplanation');
  return OFFER_UNIT_COST_COLUMN_IDS.map((id) =>
    buildCostColumn(id, t(`table.columns.${id}`, { ns: 'inventory' }), explanation),
  );
}
